import { createStore } from 'vuex'
import { connectWebSocket } from '@/utils/wsocket.js';
import wsocketMutations from '@/utils/wsocketMutations.js';
import {processReceivedData} from '@/utils/processMessagesWS.js';
import Big from 'big.js';
import { calcPL } from '../utils/funcs.js';
import { aggregateLogs, DEFAULT_AGG_OPTIONS } from '@/utils/aggregateLogs.js';
import { getPriceBin, getBinKey } from '@/utils/binning.js';
import { computePortfolioBook } from '@/components/portfolio-toolbar/domain/computePortfolioBook.js';
import { computePathPnl } from '@/utils/computePathPnl.js';
import { canonicalPurpose } from '@/utils/manualChartDays.js';

const TARGET_MARGIN_STORAGE_KEY = 'gridmaker.targetMarginPct'
const PATH_BULL_TARGET_PRICE_STORAGE_KEY = 'gridmaker.pathTargetPrice'
const PATH_BEAR_TARGET_PRICE_STORAGE_KEY = 'gridmaker.pathBearTargetPrice'
const DEFAULT_TARGET_MARGIN_PCT = 0.6

function readPositiveNumber(key, fallback = null) {
  if (typeof window === 'undefined') return fallback
  const raw = window.localStorage.getItem(key)
  if (raw == null || raw === '') return fallback
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

function persistNumber(key, value) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(key, String(value))
}

function clearPersistedNumber(key) {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(key)
}

export default createStore({
  state: {
    eventLogs: [],
    ledger: {},
    shortLedger: {},
    kpis: {},
    futuresInfos: {},
    aggregationMode: 'time',
    aggregationOptions: {},
    marketData: {btcUsd: 0, usdtBrl: 0, btcBrl: 0, priceBand: {min:0, max:0, center:0, gridSize: 0}},
    systemInfos: {systemStatus: '', strategyParams: {}},
    manualOrders: {  selectedBuys: [], selectedSells: [], selectedUnitsBySellId: {}, activeOrderIdManual: null},
    unMappedManuals: { buys: [], sells: [] },
    terminalOpenOrders: [],
    terminalCards: [],
    terminalBalances: { BTC: null, USDT: null },
    terminalError: '',
    targetMarginPct: readPositiveNumber(TARGET_MARGIN_STORAGE_KEY, DEFAULT_TARGET_MARGIN_PCT),
    pathBullTargetPrice: readPositiveNumber(PATH_BULL_TARGET_PRICE_STORAGE_KEY, null),
    pathBearTargetPrice: readPositiveNumber(PATH_BEAR_TARGET_PRICE_STORAGE_KEY, null),
  },
  mutations: {
    ...wsocketMutations,
    setTargetMarginPct(state, pct) {
      const n = Number(pct)
      state.targetMarginPct = Number.isFinite(n) && n > 0 ? n : DEFAULT_TARGET_MARGIN_PCT
      persistNumber(TARGET_MARGIN_STORAGE_KEY, state.targetMarginPct)
    },
    setPathScenarioPrice(state, { side, price } = {}) {
      const isBear = side === 'bear'
      const field = isBear ? 'pathBearTargetPrice' : 'pathBullTargetPrice'
      const key = isBear ? PATH_BEAR_TARGET_PRICE_STORAGE_KEY : PATH_BULL_TARGET_PRICE_STORAGE_KEY
      const n = Number(price)
      if (Number.isFinite(n) && n > 0) {
        state[field] = n
        persistNumber(key, n)
        return
      }
      state[field] = null
      clearPersistedNumber(key)
    },
  },
  actions: {

    startWebSocketConnection({ commit, dispatch }) {
      connectWebSocket({ commit, dispatch });
    },

    processReceivedData
  },
  getters: {
    targetPctFrac: (state) => {
      const pct = Number(state.targetMarginPct)
      return pct > 0 ? pct / 100 : DEFAULT_TARGET_MARGIN_PCT / 100
    },

    // ─── Ledger filter primitives ────────────────────────────────────────────
    activeRegularUnits: (state) => {
      return Object.values(state.ledger || {}).filter(
        u => ['pending', 'anchored'].includes(u.status) && u.bucket === 'regular'
      );
    },

    activeManualUnits: (state) => {
      return Object.values(state.ledger || {}).filter(u => u.bucket === 'manual');
    },

    regularCostByBin: (state, getters) => (gridSize) => {
      const units = getters.activeRegularUnits;
      if (!units.length) return null;

      let minPrice = Infinity, maxPrice = -Infinity;
      const costMap = new Map();

      units.forEach(u => {
        const avgPrice = Number(u.current?.avgPrice ?? 0);
        const qty     = Number(u.current?.qty ?? 0);
        if (Number.isNaN(avgPrice) || Number.isNaN(qty) || qty <= 0) return;
        if (avgPrice < minPrice) minPrice = avgPrice;
        if (avgPrice > maxPrice) maxPrice = avgPrice;
        const key = getBinKey(getPriceBin(avgPrice, gridSize));
        costMap.set(key, (costMap.get(key) || 0) + Math.round(qty * avgPrice * 100));
      });

      if (minPrice === Infinity) return null;
      const startBin = getPriceBin(minPrice, gridSize);
      const steps    = Math.round((getPriceBin(maxPrice, gridSize) - startBin) / gridSize);
      return { startBin, steps, costMap, units };
    },

    kpisDataCalcs: (state) => {
      const kpis = state.kpis || {};
      const allocatedCapitalRegulars = Number(kpis.portfolioPMPRegulars * kpis.portfolioVolumeRegulars);
      const allocatedCapitalManuals = kpis.portfolioPMPManuals * kpis.portfolioVolumeManuals;

      const plRegulars = calcPL(state.marketData.btcUsd, kpis.portfolioPMPRegulars, kpis.portfolioVolumeRegulars)
      const plManuals = calcPL(state.marketData.btcUsd, kpis.portfolioPMPManuals, kpis.portfolioVolumeManuals)
      const llpRebuys = kpis.llpRebuys

      const DeltaPLRegularsRebuys = (Math.abs(plRegulars) + Math.abs(llpRebuys)).toFixed(2);

      return { allocatedCapitalRegulars, allocatedCapitalManuals, plRegulars, plManuals, llpRebuys, DeltaPLRegularsRebuys };
    },
    aggregatedEventLogs: (state) => {
      const options = {
        ...DEFAULT_AGG_OPTIONS,
        ...state.aggregationOptions,
        mode: state.aggregationMode
      };

      return aggregateLogs(state.eventLogs || [], options);
    },

    todayProfitsMetrics: (state) => {
      return (state.eventLogs || []).reduce((acc, log) => {
        if (!log?.metrics) return acc;
        const pnl = Number(log.metrics.pnl) || 0;
        if (log.purpose === 'regularSell' || log.purpose === 'rebuyBuy') {
          acc.pnlRegular = parseFloat((acc.pnlRegular + pnl).toFixed(2));
          acc.dealCount++;
        }
        const positionPurpose = canonicalPurpose(log.purpose);
        if (positionPurpose === 'closeLong') {
            acc.pnlManual  = parseFloat((acc.pnlManual  + pnl).toFixed(2));
            acc.sellManualCount++;
        }
        if (positionPurpose === 'closeShort') {
            acc.pnlHedge  = parseFloat((acc.pnlHedge  + pnl).toFixed(2));
            acc.hedgeCount++;
        }
        return acc;
      }, { pnlRegular: 0, pnlManual: 0, pnlHedge: 0, dealCount: 0, sellManualCount: 0, hedgeCount: 0 });
    },

    allUnitsByBin: (state) => { //usado no ledgerInventory
      const ledger = state.ledger || {};
      const marketData = state.marketData || {};

      const binSize = marketData.priceBand?.gridSize || 1;
      const bins = {};

      Object.entries(ledger).forEach(([unitID, unit]) => {
        const bin = getPriceBin(unit.current.avgPrice, binSize);
        if (!bins[bin]) bins[bin] = { units: [], totalQty: 0 };

        bins[bin].units.push({ unitID, ...unit });
        bins[bin].totalQty = parseFloat(new Big(bins[bin].totalQty).plus(unit.current.qty).toString());
      });

      return bins;
    },

    // ─── regularHealthCostByBin: ledger-only health (no currentPrice dependency) ─
    // drawdown is computed in the component (Tier 2), dependent on currentPrice.
    regularHealthCostByBin: (state, getters) => (gridSize = 1000) => {
      const units = getters.activeRegularUnits;
      const ledger = state.ledger || {};

      const pendingSellRebuys = [];
      Object.entries(ledger).forEach(([unitId, unit]) => {
        if (unit.operations && Array.isArray(unit.operations)) {
          unit.operations.forEach(op => {
            if (op.type === 'sellRebuy') {
              pendingSellRebuys.push({ ...op, unitId, unitAvgPrice: Number(unit.current?.avgPrice ?? 0)
              });
            }
          });
        }
      });

      if (!units.length && !pendingSellRebuys.length) return [];

      const prices = [
        ...units.map(u => Number(u.current?.avgPrice ?? 0)),
        ...pendingSellRebuys.map(op => Number(op.price ?? 0))
      ].filter(p => !Number.isNaN(p));

      if (!prices.length) return [];

      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      const startBin = getPriceBin(minPrice, gridSize);
      const endBin   = getPriceBin(maxPrice, gridSize);
      const steps    = Math.round((endBin - startBin) / gridSize);
      const result   = [];

      for (let i = 0; i <= steps; i++) {
        const binStart = parseFloat((startBin + i * gridSize).toFixed(8));
        const binEnd   = parseFloat((binStart + gridSize).toFixed(8));
        const label    = binStart.toFixed(0);

        const binUnits = units.filter(u => {
          const p = Number(u.current?.avgPrice ?? 0);
          return p >= binStart && p < binEnd;
        });

        const binOps = pendingSellRebuys.filter(op => {
          const p = Number(op.price ?? 0);
          return p >= binStart && p < binEnd;
        });

        let totalRemainingBuyQty = new Big(0);
        let totalValorSells      = new Big(0);
        let totalValorUnits      = new Big(0);

        binOps.forEach(op => {
          const remainingBuyQty = Number(op.allocations?.remainingBuyQty ?? 0);
          const sellPrice       = Number(op.price ?? 0);
          const unitAvgPrice    = Number(op.unitAvgPrice ?? 0);
          totalRemainingBuyQty = totalRemainingBuyQty.plus(remainingBuyQty);
          totalValorSells      = totalValorSells.plus(new Big(remainingBuyQty).times(sellPrice));
          totalValorUnits      = totalValorUnits.plus(new Big(remainingBuyQty).times(unitAvgPrice));
        });

        let llp = 0;
        if (totalRemainingBuyQty.gt(0)) {
          const pmpSells = totalValorSells.div(totalRemainingBuyQty);
          const pmpUnits = totalValorUnits.div(totalRemainingBuyQty);
          llp = parseFloat(pmpSells.minus(pmpUnits).times(totalRemainingBuyQty).toFixed(2));
        }

        const allocated = binUnits.reduce((acc, u) => {
          const qty      = Number(u.current?.qty ?? 0);
          const avgPrice = Number(u.current?.avgPrice ?? 0);
          return acc.plus(new Big(qty).times(avgPrice));
        }, new Big(0));

        if (binUnits.length || binOps.length) {
          result.push({
            x:         label,
            allocated: parseFloat(allocated.toFixed(2)),
            llp,
            binUnits,           // ← passed to component for drawdown (Tier 2)
          });
        }
      }

      return result;
    },

    rebuySellDensityByBin: (state) => {
      const ledger = state.ledger || {};
      const marketData = state.marketData || {};

      const operations = [];
      Object.entries(ledger).forEach(([unitId, unit]) => {
        if (unit.operations && Array.isArray(unit.operations)) {
          unit.operations.forEach(op => {
            if (op.type === 'sellRebuy') {
              operations.push({...op, unitId, unitAvgPrice: unit.current?.avgPrice || unit.origin?.avgPrice || 0});
            }
          });
        }
      });

      if (!operations.length) return {};

      const binSize = marketData.priceBand?.gridSize || 1;
      const bins = {};

      operations.forEach(op => {
        const bin = Math.floor(op.price / binSize) * binSize;
        if (!bins[bin]) bins[bin] = { qty: 0, operations: [] };
        bins[bin].qty = parseFloat(new Big(bins[bin].qty).plus(op.qty).toString());
        bins[bin].operations.push(op);
      });

      return bins;
    },

    manualDensityByBin: (state, getters) => (gridSize) => {
      const units = getters.activeManualUnits;

      if (!units.length) return {};

      const bins = {};

      units.forEach(u => {
        if (parseFloat(u.current.qty) <= 0) return; 
        const bin = getPriceBin(u.current.avgPrice, gridSize);
        if (!bins[bin]) bins[bin] = { qty: 0, units: [] };
        bins[bin].qty = parseFloat(new Big(bins[bin].qty).plus(u.current.qty).toString());
        bins[bin].units.push(u);
      });

      return bins;
    },

    unMappedManuals: (state) => {
      // Backend já processa partial mappings e envia apenas ordens com qty > 0
      // Não precisa filtrar novamente no frontend
      return state.unMappedManuals;
    },

    manualUnits: state => {
      return Object.values(state.ledger || {}).filter(item => item.bucket === 'manual').sort((a, b) => (a.current?.avgPrice || 0) - (b.current?.avgPrice || 0));
    },

    dustRegularUnits: state => {
      return Object.values(state.ledger || {}).filter(item =>
        item.bucket === 'regular' &&
        item.status === 'pending' &&
        !(item.operations?.length) &&
        parseFloat(item.current?.qty || 0) > 0 &&
        parseFloat(item.current?.qty || 0) < 0.0001
      ).sort((a, b) => (a.current?.avgPrice || 0) - (b.current?.avgPrice || 0));
    },

    // PnL no mesmo livro da toolbar, com spot e futuros.
    // Bull/Bear Edge = PnL no preço ±5% menos o PnL no preço atual.
    directionalEdges: (state) => {
      const shock = 0.05
      const currentPrice = Number(state.marketData?.btcUsd || 0)
      const bookAt = (markPrice) => computePortfolioBook({
        ledger: state.ledger,
        shortLedger: state.shortLedger,
        futuresInfos: state.futuresInfos,
        includeFutures: true,
        markPrice,
      })
      const current = bookAt(currentPrice)
      const up = bookAt(currentPrice * (1 + shock))
      const down = bookAt(currentPrice * (1 - shock))
      const longQty = current.manualQty
      const shortQty = current.shortQty
      const totalQty = longQty + shortQty
      const pairedQty = Math.min(longQty, shortQty)
      const delta = longQty - shortQty
      const pmpLong = current.pmp
      const pmpShort = current.hedgeAvg
      const recoveryPrice = Math.abs(delta) > 1e-9
        ? (current.manualNotional - current.hedgeNotional) / delta
        : null

      return {
        longQty,
        shortQty,
        directionalBias: totalQty > 0 ? (longQty - shortQty) / totalQty : null,
        shock,
        currentPrice,
        bullPrice: currentPrice * (1 + shock),
        bearPrice: currentPrice * (1 - shock),
        pnl: current.netPnl,
        longPnl: current.longPnl,
        shortPnl: current.shortPnl,
        bullEdge: up.netPnl - current.netPnl,
        bearEdge: down.netPnl - current.netPnl,
        pairedQty,
        pmpLong,
        pmpShort,
        delta,
        recoveryPrice,
        pmpSpread: pmpShort - pmpLong,
        lockedBasisPnl: pairedQty * (pmpShort - pmpLong),
      }
    },

    pathPnl: (state, getters) => {
      const longs = Object.values(state.ledger || {})
        .filter((u) => u && u.bucket === 'manual' && Number(u.current?.qty || 0) > 0)
        .map((u) => ({
          price: Number(u.current?.avgPrice || 0),
          qty: Number(u.current?.qty || 0),
        }))
      const futLong = state.futuresInfos?.long || {}
      if (Number(futLong.qty) > 0) {
        longs.push({
          price: Number(futLong.price || 0),
          qty: Number(futLong.qty),
          closable: false,
        })
      }

      const shorts = Object.values(state.shortLedger || {})
        .filter((u) => u && u.status === 'active' && Number(u.remainingQty || 0) > 0)
        .map((u) => ({
          price: Number(u.origin?.sellPrice || 0),
          qty: Number(u.remainingQty || 0),
        }))
      const futShort = state.futuresInfos?.short || {}
      if (Number(futShort.qty) > 0) {
        shorts.push({
          price: Number(futShort.price || 0),
          qty: Number(futShort.qty),
          closable: false,
        })
      }

      const currentPrice = Number(state.marketData?.btcUsd || 0)
      const bullFallback = currentPrice > 0 ? currentPrice * 1.03 : 0
      const bearFallback = currentPrice > 0 ? currentPrice * 0.97 : 0
      const bullTargetPrice = Number(state.pathBullTargetPrice) > 0
        ? Number(state.pathBullTargetPrice)
        : bullFallback
      const bearTargetPrice = Number(state.pathBearTargetPrice) > 0
        ? Number(state.pathBearTargetPrice)
        : bearFallback
      const shared = { longs, shorts, currentPrice, targetPctFrac: getters.targetPctFrac }
      const bull = computePathPnl({ ...shared, targetPrice: bullTargetPrice })
      const bear = computePathPnl({ ...shared, targetPrice: bearTargetPrice })

      return {
        currentPrice,
        bullTargetPrice: bull.targetPrice,
        bearTargetPrice: bear.targetPrice,
        bullPathPnl: bull.pathPnl,
        bearPathPnl: bear.pathPnl,
        bullRealizedPnl: bull.realizedPnl,
        bearRealizedPnl: bear.realizedPnl,
        bullPnlChange: bull.pnlChange,
        bearPnlChange: bear.pnlChange,
        bullEndDelta: bull.endDelta,
        bearEndDelta: bear.endDelta,
        bullClosedQty: bull.closedQty,
        bearClosedQty: bear.closedQty,
      }
    },
}
});
